import React, { useCallback, useEffect } from 'react';

import { useDispatch } from 'react-redux';

import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { useLazySocialAuthenticateQuery } from '@modules/rtk/auth-api';
import toastService from '@modules/toasts/service';
import userServices from '@shared/duck/userServices';
import { intl } from '@shared/localization';
import { googleSignIn } from '@utils';

import { GOOGLE_SIGN_IN_BUTTON_ID } from '../../constants';
import {
  googleLoginValidate,
  loginError,
  loginResponse,
  logout,
} from '../../duck/actions';
import styles from './GoogleSignInButton.less';

const GoogleSignInButton: React.FC = () => {
  const dispatch = useDispatch();
  const enableGoogleSignIn = featureFlags.getFlag('ENABLE_GOOGLE_SIGN_IN');
  const [getSocialLogin] = useLazySocialAuthenticateQuery();

  const handleOnSuccess = useCallback(
    async (OAuthToken: google.accounts.id.CredentialResponse['credential']) => {
      if (!featureFlags.getFlag('SBL_6926_NEW_SESSION_LOGIC')) {
        dispatch(googleLoginValidate(OAuthToken));
        return undefined;
      }

      try {
        dispatch(googleLoginValidate());
        const loginData = await getSocialLogin({ token: OAuthToken }).unwrap();

        const loginDataWithExp = userServices.addSessionExp(loginData);
        userServices.storeLoginData(loginDataWithExp);
        userServices.storeLogoutFlag(false);
        return dispatch(loginResponse(loginDataWithExp));
      } catch (err) {
        const errTyped = err as { status: number };
        if (errTyped.status === 401) {
          toastService.error(
            intl.formatMessage({ id: `user.googleLogin.error` }),
          );
        }
        dispatch(loginError(err));
        return dispatch(logout());
      }
    },
    [dispatch, getSocialLogin],
  );

  const handleOnFailure = useCallback(() => {
    toastService.error(
      intl.formatMessage({
        id: 'user.LoginPage.error.googleLogin',
      }),
    );
  }, []);

  useEffect(() => {
    if (enableGoogleSignIn) {
      googleSignIn.init(handleOnSuccess, handleOnFailure);
    }
  }, [handleOnSuccess, handleOnFailure, enableGoogleSignIn]);

  return enableGoogleSignIn ? (
    <div id={GOOGLE_SIGN_IN_BUTTON_ID} className={styles.googleLoginButton} />
  ) : null;
};

export default GoogleSignInButton;
